import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';

const ReportSummary = ({ reportType, reportData, filters, accountId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportData || (Array.isArray(reportData) && reportData.length === 0)) {
      return;
    }

    const generateSummary = async () => {
      setLoading(true);
      setError(null);

      // Build cache key
      const cacheKey = {
        accountId,
        reportType,
        dateRangeDays: filters?.dateRange,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        correlationMetric: filters?.correlationMetric || 'ROAS',
        adNameFilter: filters?.adNameFilter
      };

      try {
        // Check cache first
        const cacheResponse = await fetch('/api/cache/summary/get', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cacheKey)
        });

        if (cacheResponse.ok) {
          const cacheData = await cacheResponse.json();
          if (cacheData.cached && cacheData.summary) {
            setSummary(cacheData.summary);
            setLoading(false);
            return; // Use cached summary
          }
        }
        // Prepare data summary based on report type
        let dataSummary = '';
        let reportName = '';

        switch (reportType) {
          case 'top-performing':
            reportName = 'Top Performing Ads';
            if (Array.isArray(reportData) && reportData.length > 0) {
              const top3 = reportData.slice(0, 3);
              dataSummary = `Top ${reportData.length} performing ads (ranked by ${filters?.correlationMetric || 'ROAS'}):
${top3.map((ad, idx) => 
  `${idx + 1}. ${ad.ad_name || 'Unknown'}: ${filters?.correlationMetric === 'revenue' ? `$${parseFloat(ad.total_revenue || 0).toFixed(0)} revenue` :
    filters?.correlationMetric === 'ctr' ? `${parseFloat(ad.ctr || 0).toFixed(2)}% CTR` :
    filters?.correlationMetric === 'spend' ? `$${parseFloat(ad.total_spend || 0).toFixed(0)} spend` :
    filters?.correlationMetric === 'cpa' ? `$${parseFloat(ad.cpa || 0).toFixed(2)} CPA` :
    `${parseFloat(ad.roas || 0).toFixed(2)}x ROAS`}, $${parseFloat(ad.total_spend || 0).toFixed(0)} spend, ${parseFloat(ad.ctr || 0).toFixed(2)}% CTR`
).join('\n')}

Total: ${reportData.length} ads, $${reportData.reduce((sum, ad) => sum + parseFloat(ad.total_spend || 0), 0).toFixed(0)} total spend, ${reportData.reduce((sum, ad) => sum + parseFloat(ad.total_revenue || 0), 0).toFixed(0)} total revenue`;
            }
            break;

          case 'ad-type-analysis':
            reportName = 'Ad Type Analysis';
            if (Array.isArray(reportData) && reportData.length > 0) {
              const top3 = reportData.slice(0, 3);
              dataSummary = `${reportData.length} ad types analyzed:
${top3.map((type, idx) => 
  `${idx + 1}. ${type.ad_type || 'Unknown'} (${type.media_type || 'Unknown'}): ${parseFloat(type.roas || 0).toFixed(2)}x ROAS, $${parseFloat(type.total_spend || 0).toFixed(0)} spend, ${parseInt(type.number_of_ads || 0)} ads`
).join('\n')}

Total: ${reportData.reduce((sum, t) => sum + parseInt(t.number_of_ads || 0), 0)} ads, $${reportData.reduce((sum, t) => sum + parseFloat(t.total_spend || 0), 0).toFixed(0)} total spend`;
            }
            break;

          case 'copy-theme-analysis':
          case 'hook-analysis':
            reportName = 'Hook Analysis';
            // Handle themes array (the actual displayed data)
            if (Array.isArray(reportData) && reportData.length > 0) {
              const top3 = reportData.slice(0, 3);
              dataSummary = `${reportData.length} hook patterns identified:
${top3.map((theme, idx) => 
  `${idx + 1}. ${theme.name || 'Pattern ' + (idx + 1)}: ${theme.metrics?.adsCount || theme.ads?.length || 0} ads, ${theme.metrics?.avgHookRate || 0}% avg hook rate, ${theme.metrics?.avgRoas || 0}x avg ROAS`
).join('\n')}

Total: ${reportData.reduce((sum, theme) => sum + (theme.metrics?.adsCount || theme.ads?.length || 0), 0)} ads analyzed`;
            } else if (reportData.patterns && Array.isArray(reportData.patterns)) {
              dataSummary = `${reportData.patterns.length} hook patterns identified:
${reportData.patterns.slice(0, 3).map((pattern, idx) => 
  `${idx + 1}. ${pattern.name || 'Pattern ' + (idx + 1)}: ${pattern.examples?.length || 0} examples, avg ROAS ${pattern.examples?.reduce((sum, ex) => sum + parseFloat(ex.roas || 0), 0) / (pattern.examples?.length || 1) || 0}`
).join('\n')}`;
            }
            break;

          case 'creative-attributes':
            reportName = 'Creative Attributes Analysis';
            // Handle the actual displayed data (array of feature combinations with avgMetric)
            // Use the same calculation as the UI to match visual numbers exactly
            const metric = filters?.correlationMetric || 'revenue';
            
            // Check if reportData has items and overallAvgMetric (new structure)
            let items = Array.isArray(reportData) ? reportData : (reportData?.items || []);
            
            // Calculate overall average - use provided value or calculate from items
            let overallAvg = 0;
            if (reportData?.overallAvgMetric) {
              overallAvg = parseFloat(reportData.overallAvgMetric);
            } else if (items.length > 0) {
              const totalMetric = items.reduce((sum, item) => {
                const itemMetric = parseFloat(item.avgMetric || 0);
                const itemCount = parseInt(item.count || 0);
                return sum + (itemMetric * itemCount);
              }, 0);
              const totalCount = items.reduce((sum, item) => sum + parseInt(item.count || 0), 0);
              overallAvg = totalCount > 0 ? totalMetric / totalCount : 0;
            }
            
            if (items.length > 0) {
              const top3 = items.slice(0, 3);
              
              dataSummary = `Top ${items.length} creative feature combinations analyzed:
${top3.map((item, idx) => {
              // Format feature name to match UI display exactly
              let featureName = '';
              if (item.combination) {
                featureName = item.combination;
              } else if (item.feature && item.value) {
                featureName = `${item.feature}: ${item.value}`;
              } else if (item.feature) {
                featureName = item.feature;
              } else {
                featureName = 'Unknown Feature';
              }
              
              const avgMetricValue = parseFloat(item.avgMetric || 0);
              const count = item.count || 0;
              
              // Calculate delta percentage exactly as UI does (matching CreativeAttributes.js line 800)
              let deltaPercent = 0;
              if (overallAvg > 0) {
                if (metric === 'cpa') {
                  // For CPA, lower is better, so invert
                  deltaPercent = ((overallAvg - avgMetricValue) / overallAvg) * 100;
                } else {
                  // For other metrics, higher is better
                  deltaPercent = ((avgMetricValue - overallAvg) / overallAvg) * 100;
                }
              }
              
              // Format metric value based on type (matching UI formatValue function)
              let metricDisplay = '';
              if (metric === 'spend' || metric === 'revenue') {
                metricDisplay = `$${avgMetricValue.toFixed(2)}`;
              } else if (metric === 'roas') {
                metricDisplay = `${avgMetricValue.toFixed(2)}x`;
              } else if (metric === 'ctr') {
                metricDisplay = `${avgMetricValue.toFixed(2)}%`;
              } else if (metric === 'cpa') {
                metricDisplay = `$${avgMetricValue.toFixed(2)}`;
              } else {
                metricDisplay = avgMetricValue.toFixed(2);
              }
              
              return `${idx + 1}. ${featureName}: ${metricDisplay} avg ${metric.toUpperCase()} (${count} ads, ${deltaPercent >= 0 ? '+' : ''}${deltaPercent.toFixed(1)}% vs average)`;
            }).join('\n')}

Overall average ${metric.toUpperCase()}: ${metric === 'spend' || metric === 'revenue' || metric === 'cpa' ? '$' : ''}${overallAvg.toFixed(2)}${metric === 'roas' ? 'x' : metric === 'ctr' ? '%' : ''}`;
            } else if (reportData.correlations && Array.isArray(reportData.correlations)) {
              const top3 = reportData.correlations.slice(0, 3);
              dataSummary = `Top ${reportData.correlations.length} feature correlations:
${top3.map((corr, idx) => {
              const correlation = parseFloat(corr.correlation || corr.r || 0);
              const featureName = corr.feature || 'Unknown';
              return `${idx + 1}. ${featureName}: ${correlation.toFixed(3)} correlation with ${metric}`;
            }).join('\n')}`;
            }
            break;

          case 'creative-breakdown':
            reportName = 'Product Breakdown';
            if (Array.isArray(reportData) && reportData.length > 0) {
              const top3 = reportData.slice(0, 3);
              dataSummary = `${reportData.length} product clusters analyzed:
${top3.map((cluster, idx) => {
              const clusterName = cluster.name || cluster.theme_name || 'Unknown';
              const roas = parseFloat(cluster.performance?.avg_roas || cluster.avg_roas || cluster.roas || 0);
              const spend = parseFloat(cluster.performance?.total_spend || cluster.total_spend || cluster.spend || 0);
              const adsCount = cluster.ads?.length || cluster.adsCount || 0;
              return `${idx + 1}. ${clusterName}: ${roas.toFixed(2)}x ROAS, $${spend.toFixed(0)} spend, ${adsCount} ads`;
            }).join('\n')}

Total: ${reportData.reduce((sum, cluster) => sum + (cluster.ads?.length || cluster.adsCount || 0), 0)} ads across all clusters`;
            }
            break;

          default:
            // Handle group-by report types (e.g., 'group-by-asset-id', 'group-by-campaign-name', etc.)
            if (reportType && reportType.startsWith('group-by-')) {
              const groupByType = reportType.replace('group-by-', '');
              const groupByDisplayNames = {
                'asset-id': 'Asset ID',
                'campaign-name': 'Campaign Name',
                'value-proposition': 'Value Proposition',
                'content-category': 'Content Category',
                'talent-quantity': 'Number of Talent',
                'talent-gender': 'Male vs Female',
                'talent-age': 'Age Range',
                'setting': 'Setting',
                'music-playing': 'Music Playing (Y/N)',
                'music-genre': 'Music Genre',
                'voiceover': 'Voiceover (Y/N)',
                'dominant-color': 'Dominant Color',
                'video-length': 'Video Length'
              };
              reportName = `${groupByDisplayNames[groupByType] || groupByType} Analysis`;
              
              if (Array.isArray(reportData) && reportData.length > 0) {
                const metric = filters?.correlationMetric || 'roas';
                const top5 = reportData.slice(0, 5);
                
                dataSummary = `${reportData.length} groups analyzed (grouped by ${groupByDisplayNames[groupByType] || groupByType}):
${top5.map((item, idx) => {
                  const groupValue = item.ad_type || item.group_value || 'Unknown';
                  const roas = parseFloat(item.roas || 0);
                  const spend = parseFloat(item.total_spend || 0);
                  const revenue = parseFloat(item.total_revenue || 0);
                  const ctr = parseFloat(item.ctr || 0);
                  const adsCount = parseInt(item.number_of_ads || 0);
                  
                  let metricValue = '';
                  if (metric === 'roas') {
                    metricValue = `${roas.toFixed(2)}x ROAS`;
                  } else if (metric === 'revenue') {
                    metricValue = `$${revenue.toFixed(0)} revenue`;
                  } else if (metric === 'spend') {
                    metricValue = `$${spend.toFixed(0)} spend`;
                  } else if (metric === 'ctr') {
                    metricValue = `${ctr.toFixed(2)}% CTR`;
                  } else if (metric === 'cpa') {
                    const cpa = parseFloat(item.cpa || 0);
                    metricValue = `$${cpa.toFixed(2)} CPA`;
                  }
                  
                  return `${idx + 1}. ${groupValue}: ${metricValue}, $${spend.toFixed(0)} spend, ${adsCount} ads, ${roas.toFixed(2)}x ROAS`;
                }).join('\n')}

Total: ${reportData.reduce((sum, item) => sum + parseInt(item.number_of_ads || 0), 0)} ads across all groups`;
              }
            } else {
              return;
            }
            break;
        }

        if (!dataSummary) {
          setLoading(false);
          return;
        }

        // Build the prompt
        const dateRangeText = filters?.dateRange ? `last ${filters.dateRange} days` : 
                             filters?.startDate && filters?.endDate ? `${filters.startDate} to ${filters.endDate}` :
                             'last 7 days';
        
        const prompt = `**TASK: Generate a concise summary with rich formatting**

You are analyzing ${reportName} data that is already queried and displayed in the UI. This is NOT a database query - analyze the provided data directly.

Report Data (already queried and displayed):
${dataSummary}

Time Period: ${dateRangeText}
Primary Metric: ${filters?.correlationMetric || 'ROAS'}

**CRITICAL INSTRUCTIONS:**
- Return ONLY markdown-formatted text (2-3 sentences)
- Use **bold** for all important numbers, metrics, and key values (currency, percentages, ROAS, CTR, counts)
- Use **# large font** (markdown heading) for creative/ad/product/feature names that should stand out
- Focus on: 1) What's performing best, 2) Key patterns, 3) One actionable takeaway
- Be specific with numbers from the data provided
- Format: Use **bold** for metrics/numbers, use # heading for names

**EXAMPLE OUTPUT FORMAT (markdown):**
Over the last **10 days**, **#Offer & Urgency** image ads dominated with **71%** of total spend (**$384K**) at **3.25x ROAS**, while **#Product Hero** studio renders showed stronger efficiency at **3.67x ROAS**. The standout performer was **#Testimonial/Product Review** videos achieving the highest **3.98x ROAS**, suggesting a significant scaling opportunity to shift budget from saturated formats into these high-performing testimonial videos.

**YOUR SUMMARY (markdown formatted text only, no code blocks):**`;

        // Call the analyze endpoint (bypasses SQL translation)
        const llmStartTime = Date.now();
        const response = await fetch('/api/chat/analyze', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: prompt,
            accountId: accountId,
            context: `report-summary-text-${reportType}`
          })
        });

        if (!response.ok) {
          throw new Error('Failed to generate summary');
        }

        const data = await response.json();
        
        // Extract text from response - analyzeDataWithLLM returns analysis field for text summaries
        let summaryText = '';
        if (data.analysis && typeof data.analysis === 'string') {
          summaryText = data.analysis;
        } else if (data.response) {
          summaryText = data.response;
        } else if (data.message) {
          summaryText = data.message;
        } else if (data.text) {
          summaryText = data.text;
        } else if (typeof data === 'string') {
          summaryText = data;
        } else if (data.result) {
          summaryText = typeof data.result === 'string' ? data.result : JSON.stringify(data.result);
        }

        // Clean up the summary (remove markdown code blocks, but keep formatting)
        summaryText = summaryText
          .replace(/```markdown\s*/g, '')
          .replace(/```\s*/g, '')
          .trim();

        if (summaryText) {
          setSummary(summaryText);
          
          // Cache the summary
          const generationTimeMs = Date.now() - llmStartTime;
          try {
            await fetch('/api/cache/summary/set', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...cacheKey,
                summaryText,
                generationTimeMs
              })
            });
          } catch (cacheError) {
            console.warn('Failed to cache summary:', cacheError);
            // Don't fail if caching fails
          }
        }
      } catch (err) {
        console.error('Error generating summary:', err);
        setError('Failed to generate summary');
      } finally {
        setLoading(false);
      }
    };

    // Debounce the summary generation
    const timeoutId = setTimeout(generateSummary, 500);
    return () => clearTimeout(timeoutId);
  }, [reportType, reportData, filters, accountId]);

  // Function to convert markdown formatting to HTML with proper styling
  const parseMarkdown = (text) => {
    if (!text) return text;
    
    let html = text;
    
    // Convert markdown headings (# text) to large bold text for names
    // Pattern: #Name or # Name
    html = html.replace(/#\s*([^\s#*]+)/g, '<span class="text-xl font-bold text-indigo-700">$1</span>');
    
    // Convert **bold** to styled spans based on content
    // First, identify what type of content is bold
    html = html.replace(/\*\*([^*]+)\*\*/g, (match, content) => {
      // Check if it's currency
      if (/^\$[\d,]+(?:\.\d+)?(?:K|M|B)?$/i.test(content.trim())) {
        return `<span class="text-emerald-600 font-bold">${content}</span>`;
      }
      // Check if it's percentage
      if (/^\d+\.?\d*%$/i.test(content.trim())) {
        return `<span class="text-blue-600 font-semibold">${content}</span>`;
      }
      // Check if it's ROAS
      if (/\d+\.?\d*x\s*ROAS/i.test(content.trim())) {
        return `<span class="text-purple-600 font-bold">${content}</span>`;
      }
      // Check if it's CTR
      if (/\d+\.?\d*%\s*CTR/i.test(content.trim())) {
        return `<span class="text-indigo-600 font-semibold">${content}</span>`;
      }
      // Check if it's a count (days, ads, etc.)
      if (/\d+\s+(?:days?|ads?|creatives?|clusters?|themes?|patterns?)/i.test(content.trim())) {
        return `<span class="text-teal-600 font-medium">${content}</span>`;
      }
      // Check if it's a large number
      if (/^\d{4,}(?:,\d{3})*$/.test(content.trim())) {
        return `<span class="text-gray-800 font-semibold">${content}</span>`;
      }
      // Default bold styling for other content
      return `<span class="text-indigo-600 font-semibold">${content}</span>`;
    });
    
    return html;
  };

  if (!reportData || (Array.isArray(reportData) && reportData.length === 0)) {
    return null;
  }

  return (
    <div className="mb-6 p-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-200/50 shadow-lg">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 mt-1">
          {loading ? (
            <div className="relative">
              <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
              <div className="absolute inset-0 bg-indigo-100 rounded-full blur-sm opacity-50"></div>
            </div>
          ) : (
            <div className="relative">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <div className="absolute inset-0 bg-indigo-200 rounded-full blur-md opacity-30 animate-pulse"></div>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-3">
            <h4 className="text-base font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              AI Summary
            </h4>
            {loading && (
              <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                Generating insights...
              </span>
            )}
          </div>
          {error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : summary ? (
            <div className="prose prose-sm max-w-none">
              <p 
                className="text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: parseMarkdown(summary) 
                }}
              />
            </div>
          ) : loading ? (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ReportSummary;

